import React, { useMemo, useRef, useCallback } from 'react';
import { Entity, LabelDefinition } from '../types';
import { getEntityColorClass, parseLabelColor } from '../utils/labelColor';
import LabelTooltip from './LabelTooltip';

export interface SelectionData {
  text: string;
  start: number;
  end: number;
  position: { top: number; left: number };
}

export interface EntitySpansProps {
  text: string;
  entities: Entity[];
  readOnly?: boolean;
  onChange?: (next: Entity[]) => void;
  customColors?: Record<string, string>;
  onEntityClick?: (entity: Entity, idx: number) => void;
  /**
   * Fired when the user highlights non-empty text inside the editable
   * container (`!readOnly`). The parent typically opens an
   * EntityTypeSelector at `position` and on label-pick appends a new
   * annotation. Restores the selection→popup flow that lived on the
   * deleted AnnotatedText component before BQ-14.
   */
  onSelectText?: (selection: SelectionData) => void;
  /** BQ-15: when readOnly && onPromote is set, render a hover ✓ button that
   *  promotes this prediction into humanAnnotations. */
  onPromote?: (entity: Entity, idx: number) => void;
  /** BQ-15: when readOnly && onReject is set, render a hover ✗ button that
   *  rejects this prediction (struck through, dropped from FP counts). */
  onReject?: (entity: Entity, idx: number) => void;
  /** BQ-15: predicate — render a green border / check badge when true. */
  isPromoted?: (entity: Entity) => boolean;
  /** BQ-15: predicate — render strikethrough + 50% opacity when true. */
  isRejected?: (entity: Entity) => boolean;
  /**
   * BQ-18: per-label annotation guidelines. When undefined or the lookup is
   * empty, span rendering is unchanged — the LabelTooltip wrapper is a
   * pass-through.
   */
  labelDefinitions?: Record<string, LabelDefinition>;
}

// Entity carrying its index in the original `entities` array. We carry this
// through the merge step so React keys can use a stable, collision-free
// identity (origIdx) — using `${start}-${end}` collides on overlaps, which is
// exactly the case the merge handles.
interface IndexedEntity {
  entity: Entity;
  origIdx: number;
}

interface MergedSegment {
  start: number;
  end: number;
  entities: IndexedEntity[]; // ordered: outermost (longest, lowest start) first
}

/**
 * Merge potentially-overlapping intervals into a non-overlapping cover so
 * we can render text as a flat sequence of spans.
 *
 * We do NOT drop overlapping entities. Instead, when an interval extends
 * past an active interval's end, the trailing tail still produces a span;
 * the entity that "owns" the visible span is the most-recently-started
 * interval that still covers that range (outermost-first ordering keeps
 * the original entity available too, e.g. for parent-decided UI).
 *
 * Returns segments in start-asc order, each with the list of entities
 * that overlap that segment.
 */
function mergeEntitySegments(
  text: string,
  indexed: IndexedEntity[]
): MergedSegment[] {
  if (indexed.length === 0) return [];

  // Stable sort by (start asc, end desc). Identical start → longer interval
  // first; that puts the "outer" interval before its inner overlaps.
  const sorted = [...indexed].sort((a, b) => {
    const aStart = a.entity.start ?? 0;
    const bStart = b.entity.start ?? 0;
    if (aStart !== bStart) return aStart - bStart;
    const aEnd = a.entity.end ?? aStart + a.entity.text.length;
    const bEnd = b.entity.end ?? bStart + b.entity.text.length;
    return bEnd - aEnd; // longer first
  });

  // Sweep all unique boundaries, then for each segment record which
  // entities actively cover it.
  const boundaries = new Set<number>();
  sorted.forEach(({ entity }) => {
    const start = clampOffset(entity.start ?? 0, text.length);
    const end = clampOffset(
      entity.end ?? (entity.start ?? 0) + entity.text.length,
      text.length
    );
    if (end <= start) return;
    boundaries.add(start);
    boundaries.add(end);
  });

  const sortedBoundaries = Array.from(boundaries).sort((a, b) => a - b);
  const segments: MergedSegment[] = [];

  for (let i = 0; i < sortedBoundaries.length - 1; i++) {
    const segStart = sortedBoundaries[i];
    const segEnd = sortedBoundaries[i + 1];
    if (segEnd <= segStart) continue;

    const covering = sorted.filter(({ entity }) => {
      const s = clampOffset(entity.start ?? 0, text.length);
      const e = clampOffset(
        entity.end ?? (entity.start ?? 0) + entity.text.length,
        text.length
      );
      return s <= segStart && e >= segEnd;
    });

    if (covering.length === 0) continue;

    segments.push({
      start: segStart,
      end: segEnd,
      entities: covering
    });
  }

  return segments;
}

function clampOffset(n: number, max: number): number {
  if (!Number.isFinite(n)) return 0;
  if (n < 0) return 0;
  if (n > max) return max;
  return Math.floor(n);
}

const EntitySpans: React.FC<EntitySpansProps> = ({
  text,
  entities,
  readOnly = false,
  onChange,
  customColors,
  onEntityClick,
  onSelectText,
  onPromote,
  onReject,
  isPromoted,
  isRejected,
  labelDefinitions,
}) => {
  // Acknowledge unused-but-typed prop without breaking the contract.
  // Parents will use onChange via onEntityClick edits / external buttons.
  void onChange;

  const containerRef = useRef<HTMLSpanElement>(null);

  // Capture text-highlight → popup. Restores the selection-to-create flow
  // that lived on the deleted AnnotatedText pre-BQ-14. Per BQ-5's intent,
  // we use DOM-derived offsets as authoritative — no case-insensitive
  // indexOf fallback that silently writes wrong offsets on repeated tokens.
  const handleMouseUp = useCallback(() => {
    if (readOnly || !onSelectText) return;
    const selection = window.getSelection();
    if (!selection || selection.isCollapsed) return;
    const container = containerRef.current;
    if (!container) return;
    const rawSelected = selection.toString();
    if (!rawSelected.trim()) return;

    const range = selection.getRangeAt(0);
    if (!container.contains(range.commonAncestorContainer)) return;

    // Compute the char-offset of the selection START relative to the
    // container, by cloning the range and extending its start to the
    // container origin.
    const preRange = range.cloneRange();
    preRange.selectNodeContents(container);
    preRange.setEnd(range.startContainer, range.startOffset);
    const startRaw = preRange.toString().length;
    const endRaw = startRaw + rawSelected.length;

    // Trim leading/trailing whitespace from the selection so the stored
    // offsets match the visible token, not whatever the mouse-drag picked up.
    const leadingWs = (rawSelected.match(/^\s*/) || [''])[0].length;
    const trailingWs = (rawSelected.match(/\s*$/) || [''])[0].length;
    const start = startRaw + leadingWs;
    const end = endRaw - trailingWs;
    if (end <= start) return;

    const trimmedText = text.substring(start, end);
    if (trimmedText.length === 0) return;

    const rect = range.getBoundingClientRect();
    onSelectText({
      text: trimmedText,
      start,
      end,
      position: { top: rect.bottom + 5, left: rect.left },
    });
  }, [readOnly, onSelectText, text]);

  const indexed: IndexedEntity[] = useMemo(
    () => entities.map((entity, origIdx) => ({ entity, origIdx })),
    [entities]
  );

  const segments = useMemo(
    () => mergeEntitySegments(text, indexed),
    [text, indexed]
  );

  if (entities.length === 0 || segments.length === 0) {
    return (
      <span
        ref={containerRef}
        className="whitespace-pre-wrap"
        onMouseUp={handleMouseUp}
      >
        {text}
      </span>
    );
  }

  const nodes: JSX.Element[] = [];
  let cursor = 0;

  segments.forEach((seg, segIdx) => {
    if (seg.start > cursor) {
      nodes.push(
        <span key={`gap-${cursor}-${seg.start}`}>{text.substring(cursor, seg.start)}</span>
      );
    }

    // The outer-most entity in the segment "owns" the visible coloring.
    // Inner overlaps are still tracked (seg.entities) so callers can opt
    // into stack visuals later; we use the outermost for click target by
    // default to mirror the prior single-color rendering behavior.
    const owner = seg.entities[0];
    const owningEntity = owner.entity;
    const owningOrigIdx = owner.origIdx;

    const colorClass = getEntityColorClass(owningEntity.label, customColors);
    const isCustomColor =
      colorClass.includes('|') || colorClass.startsWith('#') || colorClass.startsWith('rgb');
    const parsed = isCustomColor ? parseLabelColor(colorClass) : null;

    // BQ-15: promote/reject visual states. Promoted gets a green ring +
    // small check badge; rejected gets strikethrough + 50% opacity.
    const promoted = isPromoted ? isPromoted(owningEntity) : false;
    const rejected = isRejected ? isRejected(owningEntity) : false;

    const baseClass = isCustomColor
      ? 'inline-block px-1 py-0.5 rounded font-medium text-xs border-2'
      : `${colorClass} px-1 py-0.5 rounded`;
    const interactiveClass = readOnly ? '' : ' cursor-pointer';
    const stateClass =
      (promoted ? ' ring-2 ring-green-500 ring-offset-1' : '') +
      (rejected ? ' line-through opacity-50' : '');

    // BQ-15: hover ✓/✗ controls only on read-only rows (model rows). The
    // group/group-hover Tailwind pattern keeps the buttons hidden until the
    // wrapping span is hovered.
    const showHoverControls = readOnly && (!!onPromote || !!onReject);

    // BQ-18: optional tooltip wrapper. When labelDefinitions is undefined or
    // the lookup is empty, LabelTooltip renders children with no popover.
    const definition = labelDefinitions?.[owningEntity.label];

    if (showHoverControls) {
      nodes.push(
        <LabelTooltip
          key={`entity-wrap-${owningOrigIdx}-${segIdx}`}
          label={owningEntity.label}
          definition={definition}
        >
          <span className="group relative inline-block">
            <span
              className={(baseClass + interactiveClass + stateClass).trim()}
              style={
                parsed
                  ? {
                      backgroundColor: parsed.bg,
                      color: parsed.text,
                      borderColor: parsed.border,
                    }
                  : undefined
              }
              onClick={
                readOnly || !onEntityClick
                  ? undefined
                  : (e) => {
                      e.stopPropagation();
                      onEntityClick(owningEntity, owningOrigIdx);
                    }
              }
            >
              {text.substring(seg.start, seg.end)}
            </span>
            <span
              className="hidden group-hover:inline-flex absolute -top-2 -right-2 items-center gap-0.5 z-10"
              // Stop the wrapping span's click handlers from firing through the
              // hover-only buttons — defense in depth, since the buttons set
              // their own onClick + stopPropagation.
              onClick={(e) => e.stopPropagation()}
            >
              {onPromote && !promoted && (
                <button
                  type="button"
                  aria-label="Promote prediction to gold"
                  title="Promote to gold"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none shadow"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPromote(owningEntity, owningOrigIdx);
                  }}
                >
                  ✓
                </button>
              )}
              {onReject && !rejected && (
                <button
                  type="button"
                  aria-label="Reject prediction"
                  title="Reject prediction"
                  className="bg-red-600 hover:bg-red-700 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] leading-none shadow"
                  onClick={(e) => {
                    e.stopPropagation();
                    onReject(owningEntity, owningOrigIdx);
                  }}
                >
                  ✗
                </button>
              )}
            </span>
          </span>
        </LabelTooltip>
      );
    } else {
      nodes.push(
        <LabelTooltip
          key={`entity-${owningOrigIdx}-${segIdx}`}
          label={owningEntity.label}
          definition={definition}
        >
          <span
            className={(baseClass + interactiveClass + stateClass).trim()}
            style={
              parsed
                ? {
                    backgroundColor: parsed.bg,
                    color: parsed.text,
                    borderColor: parsed.border,
                  }
                : undefined
            }
            onClick={
              readOnly || !onEntityClick
                ? undefined
                : (e) => {
                    e.stopPropagation();
                    onEntityClick(owningEntity, owningOrigIdx);
                  }
            }
          >
            {text.substring(seg.start, seg.end)}
          </span>
        </LabelTooltip>
      );
    }

    cursor = seg.end;
  });

  if (cursor < text.length) {
    nodes.push(<span key={`gap-${cursor}-end`}>{text.substring(cursor)}</span>);
  }

  return (
    <span
      ref={containerRef}
      className="whitespace-pre-wrap"
      onMouseUp={handleMouseUp}
    >
      {nodes}
    </span>
  );
};

export default EntitySpans;
